/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import * as d3 from 'd3';
import type { i18n, TFunction } from 'i18next';
import type { ScalesType } from './types';
import type { SoundingDiagramType } from '../../api/edr-api/types';
import {
  bijvoetInterpolationPres,
  bijvoetInterpolationTheta,
  calculateThetaFromPressTemp,
  pressToPascal,
  tempToKelvin,
} from '../StaticLines/bijvoetUtils';
import { isobarValues } from '../StaticLines/drawIsobars';

export const SOUNDING_COLORS = [
  '#4487f3',
  '#c3213c',
  '#509a00',
  '#ED6C06',
  '#9810FA',
  '#FFDF20',
  '#312C85',
];

export interface Margin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface InitializeSvg {
  svgRef: React.MutableRefObject<SVGSVGElement | null>;
  width: number;
  height: number;
  margin: Margin;
  diagramType: SoundingDiagramType;
  d3GraphId: string;
}

interface DrawAxis {
  svg: d3.Selection<SVGElement, unknown, HTMLElement, undefined>;
  xScale: ScalesType;
  yScale: ScalesType;
  height: number;
  diagramType: SoundingDiagramType;
}

interface PlotSounding {
  temperatureValues: number[];
  pressureValues: number[];
  color: string;
  lineStyle: string;
  xScale: ScalesType;
  yScale: ScalesType;
  height: number;
  svg: d3.Selection<SVGElement, unknown, HTMLElement, undefined>;
  diagramType: SoundingDiagramType;
}

export const getDiagramName = (
  t: TFunction<'soundings', i18n>,
  diagramType: SoundingDiagramType,
): string => {
  switch (diagramType) {
    case 'BIJVOET': {
      return t('diagram-BIJVOET');
    }
    case 'EMAGRAM': {
      return t('diagram-EMAGRAM');
    }

    default: {
      return t('diagram-SKEWT');
    }
  }
};

// vfx code
export const initializeSvg = ({
  svgRef,
  width,
  height,
  margin,
  diagramType,
  d3GraphId,
}: InitializeSvg): d3.Selection<SVGGElement, unknown, null, undefined> => {
  const svg = d3
    .select(svgRef.current)
    .attr('width', width + (margin.left + margin.right))
    .attr('height', height + (margin.top + margin.bottom))
    .attr('id', `${d3GraphId}-${diagramType}`)
    .attr('data-testid', diagramType)
    .append('g')
    .attr(
      'transform',
      // Use only left margin for horizontal translation; adding right margin here shifts
      // the plotting group away from the clipPath (defined at x=0) and breaks left-edge clipping.
      `translate(${margin.left},${margin.top})`,
    );

  svg
    .append('clipPath')
    .attr('id', `clip-${d3GraphId}-${diagramType}d3`)
    // Ensure clipPath uses same coordinate system as the translated group
    // so width/height starting at x=0,y=0 align with axis origin.
    .attr('clipPathUnits', 'userSpaceOnUse')
    .append('rect')
    .attr('width', width)
    .attr('height', height)
    .attr('x', 0)
    .attr('y', 0);

  svg
    .append('rect')
    .attr('height', height)
    .attr('width', width)
    .attr('fill', '#ffffff00')
    .attr('opacity', 1)
    .attr('id', `${d3GraphId}-${diagramType}d3`)
    .attr('clip-path', `url(#clip-${d3GraphId}-${diagramType}d3)`);

  return svg;
};

export const createScales = (
  diagramType: SoundingDiagramType,
  width: number,
  height: number,
  minPressure: number,
  maxPressure: number,
  minTemperature: number,
  maxTemperature: number,
): [ScalesType, ScalesType] => {
  if (diagramType === 'BIJVOET') {
    // Note, for Bijvoet, the x-axis is NOT temperature but theta_es
    // Both axis aren't linear nor logarithmic, hence we need an interpolation function to determine (x,y) based on (theta_es,p)
    // These scales work the same as for SkewT -> you pass in a pressure (or theta value) and you receive the location in the svg
    const xScale = d3
      .scaleLinear()
      .domain([0, 1])
      .range([0, width])
      .interpolate(bijvoetInterpolationTheta);
    const yScale = d3
      .scaleLinear()
      .domain([1, 0])
      .range([height, 0])
      .interpolate(bijvoetInterpolationPres);

    return [xScale, yScale];
  }
  const xScale = d3
    .scaleLinear()
    .domain([minTemperature, maxTemperature])
    .range([0, width]);
  const yScale = d3
    .scaleLog()
    .domain([maxPressure, minPressure])
    .range([height, 0]);

  return [xScale, yScale];
};
export const drawAxes = ({
  svg,
  xScale,
  yScale,
  height,
  diagramType,
}: DrawAxis): void => {
  // For Bijvoet, we don't show an x-axis as we're interested in the isotherms only, not theta
  if (diagramType !== 'BIJVOET') {
    // Create x-axis with intervals
    const xTicks = xScale.ticks(10);

    const xNegativeDisplacement = (): string => {
      if (diagramType === 'SKEWT') {
        return '-0.25em';
      }

      if (diagramType === 'EMAGRAM') {
        return '-.5em';
      }

      return '0em';
    };

    const xPositiveDisplacement = (): string => {
      if (diagramType === 'SKEWT') {
        return '-0.2em';
      }

      if (diagramType === 'EMAGRAM') {
        return '0.0em';
      }

      return '0em';
    };

    const xStringToSignedNumber = (xString: string): number => {
      const str = xString.replace(/[^\d.-]/g, '');
      const xStringHasSomeLeadingCharacters = xString.length > str.length;
      const xValue = xStringHasSomeLeadingCharacters
        ? -parseInt(str, 10)
        : parseInt(xString, 10);
      return xValue;
    };

    svg
      .append('g')
      .attr('type', 'x-axis')
      .attr('transform', `translate(0,${height})`)
      .call(
        d3
          .axisBottom(xScale)
          .tickValues(xTicks)
          .tickSizeInner(0)
          .tickSizeOuter(0),
      );

    // Reformat in the case of the zero tick label
    // to add the unit part
    svg
      .selectAll('g[type="x-axis"] .tick')
      .filter(function isZero(): boolean {
        const xString = d3.select(this).select('text').text();
        const xValue = xStringToSignedNumber(xString);
        return xValue === 0; // Always true for SkewT and Emagram as they have a 0°C crossing
      })
      .each(function txt(): void {
        const unitPart = '°C';
        d3.select(this)
          .select('text')
          // tspan necessary to use a different font-size
          .append('tspan')
          .text(unitPart)
          .attr('font-size', '11px')
          .attr('dy', '0.04em')
          .attr('dx', '0.0em');
      });

    svg
      .selectAll('g[type="x-axis"] .tick')
      .filter(function isLessThanZero(): boolean {
        const xString = d3.select(this).select('text').text();
        const xValue = xStringToSignedNumber(xString);
        return xValue < 0;
      })
      .each(function txt(): void {
        d3.select(this).select('text').attr('dx', xNegativeDisplacement());
      });

    svg
      .selectAll('g[type="x-axis"] .tick')
      .filter(function isGreaterThanZero(): boolean {
        const xString = d3.select(this).select('text').text();

        const xValue = xStringToSignedNumber(xString);
        return xValue > 0;
      })
      .each(function txt(): void {
        d3.select(this).select('text').attr('dx', xPositiveDisplacement());
      });

    const xMiddleValue = xTicks[xTicks.length - 1];

    svg
      .selectAll('g[type="x-axis"] .tick')
      .filter(function isLast(): boolean {
        const xString = d3.select(this).select('text').text();

        const xValue = xStringToSignedNumber(xString);
        return xValue === xMiddleValue;
      })
      .each(function txt(): void {
        d3.select(this).select('text').attr('dx', xNegativeDisplacement());
      });
  }

  // Create y-axis with intervals
  // Make sure all zeroes are printed (1000 vs 1k)
  const formatTicks = d3.format('.0f');
  svg
    .append('g')
    .attr('type', 'y-axis')
    .call(
      d3
        .axisLeft(yScale)
        .tickValues(isobarValues)
        .tickFormat(formatTicks)
        .tickSizeInner(0)
        .tickSizeOuter(0),
    )
    .call((gee) => gee.select('.domain').remove())
    .append('text')
    .attr('fill', 'currentColor')
    .attr('x', '0')
    .attr('y', yScale(400))
    .attr('font-size', '11px')
    .attr('dy', '-0.9em')
    .attr('text-anchor', 'left')
    .text('hPa');
};

export const mapToPlot = (
  diagramType: SoundingDiagramType,
  filteredPressureValues: number[],
  filteredTemperatureValues: number[],
  xScale: ScalesType,
  yScale: ScalesType,
  height: number,
): [number, number][] => {
  if (diagramType === 'BIJVOET') {
    const thetaValues = filteredTemperatureValues.map(
      (tempValue: number, index: number) =>
        calculateThetaFromPressTemp(
          pressToPascal(filteredPressureValues[index]),
          tempToKelvin(tempValue),
        ),
    );

    return d3
      .zip(filteredPressureValues, thetaValues)
      .map(([pressure, theta]) => [xScale(theta), yScale(pressure)]);
  }
  const Px = d3
    .zip(filteredPressureValues, filteredTemperatureValues)
    .map(([pressure, temperature]) => [xScale(temperature), yScale(pressure)]);

  if (diagramType === 'EMAGRAM') {
    return Px.map(([x, y]) => [x, y]);
  }

  // Skew/shear transformation for skew-t
  return Px.map(([x, y]) => [x + height - y, y]);
};

export const plotSounding = ({
  temperatureValues,
  pressureValues,
  color,
  lineStyle,
  xScale,
  yScale,
  height,
  svg,
  diagramType,
}: PlotSounding): void => {
  const [filteredPressureValues, filteredTemperatureValues] =
    filterDomainRangeForParameter(pressureValues, temperatureValues);

  // Map temp and pressure to pixels
  const Px = mapToPlot(
    diagramType,
    filteredPressureValues,
    filteredTemperatureValues,
    xScale,
    yScale,
    height,
  );

  const lineGenerator = d3.line();
  const pathString = lineGenerator(Px);

  svg
    .append('path')
    .attr('d', pathString)
    .attr('id', 'dataline')
    .attr('x', 0)
    .attr('y', 0)
    .style('fill', 'none')
    .style('stroke-width', 2)
    .style('stroke', color)
    .style('stroke-opacity', 1)
    .style('stroke-dasharray', lineStyle)
    .attr('clip-path', `url(#clip-${diagramType}d3)`);
};

// Use this function to filter the pressure and parameter array so no NaN or null values are included
export const filterDomainRangeForParameter = (
  pressureDomain: number[],
  parameterDomain: number[],
): [number[], number[]] => {
  const filteredPressureDomain = pressureDomain.filter(
    (_value, index) =>
      !isNaN(parameterDomain[index]) && parameterDomain[index] !== null,
  );
  const filteredParameterDomain = parameterDomain.filter(
    (value) => !isNaN(value) && value !== null,
  );
  return [filteredPressureDomain, filteredParameterDomain];
};
